import { Group, Stack, Text } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { IDnRouteInfo } from "../../../models/DeliveryNote"
import { Dot } from "../../delivery/atoms"

const SIZE_RANK: Record<string, number> = {
  "1S": 1, "2S": 2, "3S": 3, "4S": 4, "5S": 5, "6S": 6,
  "7S": 7, "8S": 8, "9S": 9, "10S": 10, "12S": 12, "15S": 15,
}

export type CapacityState = "DEFAULT" | "WARNING" | "DANGER"

export function deriveCapacityState(
  dnSize: number | string | null | undefined,
  vehicleSizeClass: string | null | undefined,
): CapacityState {
  const v = vehicleSizeClass ? SIZE_RANK[vehicleSizeClass] ?? 0 : 0
  const d = typeof dnSize === "number" ? dnSize : dnSize ? Number(dnSize) || 0 : 0
  if (!v || !d) return "DEFAULT"
  if (d > v) return "DANGER"
  if (d === v) return "WARNING"
  return "DEFAULT"
}

interface Props {
  route: IDnRouteInfo
  dnSize: number | string | null | undefined
}

/** Plate (mono, bold) + «model (class)»; capacity overflow as a dot line, never a red box. */
export function VehicleCell({ route, dnSize }: Props) {
  const { t } = useTranslation("delivery")
  const v = route.vehicle
  if (!v) return null
  const state = deriveCapacityState(dnSize, v.sizeClass)
  const model = [v.make, v.model].filter(Boolean).join(" ")
  const cls = v.vehicleClass ? t(`vehicle.class.${v.vehicleClass}`, { defaultValue: "" }) : ""
  return (
    <Stack gap={2} miw={0}>
      <Text ff="monospace" fw={650} fz="var(--nw-fz-mono)" truncate="end">
        {v.plateNumber}
      </Text>
      {(model || cls) && (
        <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)" truncate="end" title={cls ? `${model} (${cls})` : model}>
          {model}
          {cls ? ` (${cls})` : ""}
        </Text>
      )}
      {state !== "DEFAULT" && (
        <Group gap={6} wrap="nowrap">
          <Dot tone={state === "DANGER" ? "red" : "amber"} />
          <Text fz="var(--nw-fz-body)">
            {state === "DANGER" ? t("active.capacityExceeded") : t("manage.active.capacityFull")}
          </Text>
        </Group>
      )}
    </Stack>
  )
}
